import { Component, booleanAttribute, computed, input, numberAttribute } from '@angular/core';
import { findPlateCountry } from './plate-countries';
import { findRegionEmblem } from './plate-emblems';

/**
 * 'emblem' — a coat of arms / seal sits between region and number (AT, DE, CH, SI).
 * 'dash'   — the halves are separated by a hyphen (SK).
 * 'none'   — the country has no region prefix; the number renders undivided (CZ, HU, IT).
 */
export type RegionSeparator = 'emblem' | 'dash' | 'none';

/**
 * Renders a European license plate.
 *
 * Built in-house rather than pulled from a package: the only display library available is
 * React-only (AO19/react-license-plate) and the other candidate (niklaswa/license-plate-generator)
 * is a server-side PNG generator for German plates. Both are the wrong shape for an Angular form
 * that has to repaint on every keystroke, so this is CSS + text — no image, no request, and the
 * number stays selectable and screen-reader readable.
 *
 * Austrian layout follows the specification in force since 2002-11-01: black on white, blue EU
 * band on the left carrying the ring of stars and the country code, and **red-white-red tribands
 * along the top and bottom edges** (the national colours). Non-Austrian plates render without the
 * tribands, since those are specific to AT.
 *
 * The emblem between district code and number is the arms of the **Bundesland** the district
 * belongs to — Upper Austria for "L", Vienna for "W" — resolved through {@link findAtState}. It
 * is explicitly *not* the national red-white-red, which is what this component drew until
 * Albert's 2026-07-29 requirement mail ("after the district you MUST NOT show the Austrian flag").
 */
@Component({
  selector: 'app-plate',
  standalone: true,
  templateUrl: './plate.component.html',
  styleUrl: './plate.component.scss',
  host: {
    '[class.lp--sm]': 'size() === "sm"',
    '[class.lp--lg]': 'size() === "lg"',
    '[class.lp--austrian]': 'isAustrian()',
    '[class.lp--editable]': 'editable()',
    // Drives the width in `--lp-chars` units; null lets the stylesheet's own default apply.
    '[style.--lp-chars]': 'chars() || null'
  }
})
export class PlateComponent {
  /** The plate number, already normalised (uppercase, no separators). */
  readonly number = input<string>('');
  /**
   * Country code shown on the EU band, e.g. 'A', 'D', 'CZ'.
   *
   * Empty by default, which hides the blue band altogether. A plate component that has not been
   * told a country must not invent one: the home page asks for a plate without offering a country
   * chooser, and defaulting to 'A' there drew an Austrian plate around a number that may well be
   * German or Czech.
   */
  readonly euBand = input<string>('');
  /**
   * District/region code, e.g. 'L' (Linz) or 'LL' (Linz-Land). When set on an Austrian plate the
   * number is rendered as `L ⬥ 1234PP` — the emblem marks where the state coat of arms sits on a
   * real plate. Purely presentational: what gets stored is always the separator-free number.
   */
  readonly region = input<string>('');
  /**
   * How the region is set off from the number: an emblem (AT/DE/CH/SI carry a coat of arms or
   * seal there), a hyphen (SK), or 'none' for countries with no district prefix at all.
   *
   * Defaults to 'none' — with no country known there is no district convention to apply, and
   * splitting the number on a guess would be worse than leaving it whole.
   */
  readonly regionSeparator = input<RegionSeparator>('none');
  /**
   * ISO country code — decides whether the Austrian tribands are drawn and which region table
   * supplies the arms. Empty by default, for the same reason as {@link euBand}.
   */
  readonly countryCode = input<string>('');
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  /** Shown in place of the number while the field is empty (preview only). */
  readonly placeholder = input<string>('');
  /**
   * Renders projected content in the white field instead of {@link number} — used to put the
   * real `<input>` inside the plate, so typing happens on the plate rather than next to it.
   */
  readonly editable = input(false, { transform: booleanAttribute });
  /**
   * Number of characters to size the plate for, so an editable plate grows and shrinks with what
   * has actually been typed instead of sitting at one fixed width — "not too big, not too small".
   *
   * 0 (the default) keeps the old behaviour: fill the container up to the stylesheet's maximum.
   * The stylesheet clamps the result, so a one-character plate still looks like a plate and a
   * 32-character paste cannot stretch it across the page.
   */
  readonly chars = input(0, { transform: numberAttribute });

  protected readonly isAustrian = computed(() => this.countryCode().toUpperCase() === 'AT');
  protected readonly display = computed(() => this.number() || this.placeholder());
  protected readonly isPlaceholder = computed(() => !this.number() && !!this.placeholder());

  /**
   * The number split at the district code, for the `L ⬥ 1234PP` rendering. Falls back to a single
   * undivided part whenever the region is unknown or does not actually prefix the number, so a
   * mismatched region can never silently corrupt what is shown.
   */
  private readonly rawParts = computed(() => {
    const number = this.number();
    const region = this.region().toUpperCase();
    if (this.regionSeparator() === 'none'
      || !region
      || !number.startsWith(region)
      || number.length === region.length) {
      return { region: '', rest: number };
    }
    return { region, rest: number.slice(region.length) };
  });

  /**
   * The split actually rendered.
   *
   * Collapses back to one undivided run when the country separates with an emblem but we have no
   * arms for this particular region — an unknown Austrian district, or a country whose region
   * table has not been written yet. There is nothing to put between the two halves, and a gap
   * where a shield should be reads as a rendering fault.
   */
  protected readonly parts = computed(() => {
    const raw = this.rawParts();
    if (this.regionSeparator() === 'emblem' && !this.arms()) {
      return { region: '', rest: raw.region + raw.rest };
    }
    return raw;
  });

  protected readonly hasRegion = computed(() => !!this.parts().region);
  protected readonly showEmblem = computed(() => this.regionSeparator() === 'emblem');

  /**
   * The regional arms drawn between region code and number — Upper Austria's for "L", Vienna's
   * for "W". `undefined` when the country has no region data or the code is not a known region;
   * the template then falls back to a neutral shield rather than inventing an emblem.
   */
  protected readonly arms = computed(() =>
    findRegionEmblem(findPlateCountry(this.countryCode())?.regions, this.rawParts().region));

  /** Whether the blue EU band is drawn at all — it is not, when no country is known. */
  protected readonly hasBand = computed(() => !!this.euBand());

  /** Twelve star angles (radians), starting at 12 o'clock. */
  protected readonly stars = Array.from({ length: 12 }, (_, i) => (i * Math.PI) / 6 - Math.PI / 2);

  protected cos(radians: number): number {
    return Math.cos(radians);
  }

  protected sin(radians: number): number {
    return Math.sin(radians);
  }
}
